import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { ModalCreateEditExtraBig } from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../../../baseComponents/ModalsLayouts/types'
import { RowData } from '../../../../../../../../baseComponents/TwinTable/types'
import { ColumnConfiguration, ColumnTableSchema } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import InputQuantityButtons from '../../../../../../../../forms/Input/InputQuantityButtons'
import { BeforeSubmitHandler } from '../../../../../../../../forms/TwinForm/types'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { TwinDictionary, dictionaryComplexFromJsonArr } from '../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import TableWithoutVirtualListing from '../../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { checkIfScale, filterElementsBySearchEditAddonProducts, showCheckboxUpdateQuantity, sortTableDataByNLines, updateQuantityScaled, updateQuantityScaledVariables } from './functions'
import { AddonProductOrderTy, DictEditProductAddonOrder } from './types'
import { CheckboxMargin } from '../../../../../../../../forms/Checkbox'
import { InputTableURLsTable } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import SearchBar from '../../../../../../../../baseComponents/SearchBar'
import { getCategoriesDictionary } from '../../../../../../../../utils/reducers/getters'
import { CategoriesPayload } from '../../../../../../../../utils/reducers/products/categories'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../../../../utils/reducers'
import SearchBarProduct from '../../../../../../../Product/SearchBarProduct'
import { OnChangeSearchBarAddProduct } from '../../../../../../../Product/SearchBarProduct/types'
import './modalEditAddonProductOrder.sass'
import { SpecificPriceRulesPayload } from '../../../../../../../../utils/reducers/products/specificPriceRules'
import { DisplayPriceGettingSpecificPriceParams } from '../../../../../../../../baseComponents/Displays/DisplayPrice'
import { roundNumberAndDecimals } from '@teinor/erp/prices'
import { baseCalculateParams, totalCalculateParams } from '@teinor/erp/prices/quote/calculate'
import { CustomSelectWithSearchBar } from '../../../../../../../../forms/CustomSelect'

type ModalComplementProductOrderProps = ModalComplementProductOrderLogicProps & ModalEditComponentProps & ReduxOrderSellLayout & {
    inputTableURLs: InputTableURLsTable
}

const ModalComplementProductOrder: React.FC<ModalComplementProductOrderProps> = ({ setOpened, allRowData, onSubmit, inputTableURLs, specificPriceRules, ...rest }) => {
    const { data, beforeSubmit, setData, searchValue, setSearchValue, onAddAddonProduct, specificPriceRule, mySpecificPriceRuleOnChange } = useModalComplementProductOrderLogic({ ProductId: allRowData?.ProductId, allRowData: allRowData || {}, myOrderId: rest.myOrderId })
    const { t } = useTwinTranslation()
    const renderThis: JSX.Element[] = []
    if(data){
        for (const key in data?.productAddons) {
            const element = data?.productAddons?.[key as any]
            if (key !== 'ParentProduct') {
                renderThis.push(
                    <SingleTableAddon data={data} elements={element.elements} setData={setData} categoryName={element.name} categoryOrder={key} key={key} searchValue={searchValue} specificPriceRules={specificPriceRules} type={allRowData?.myType} {...{allRowData}} />
                )
            }
        }
    }
    return (
        <ModalCreateEditExtraBig fields={[]} url={inputTableURLs.createData[0]} setOpened={setOpened} translations={{ title: t('addons', 'Complementos'), button: t('save', 'Guardar') }} haveButtonPermissions={true} {...rest} onSubmit={onSubmit} beforeSubmit={beforeSubmit} className='addon_create_modal'>
            {data? <ParentProduct data={data} setData={setData} /> : null}
            <div className={'grid grid-cols-3 mt-10 justify-between ' + (allRowData?.myType === 'Sell'?'' :'')}>
                <SearchBar placeholder={t('searchDots', 'Buscar...')} searchValue={searchValue} onChange={(value) => setSearchValue(value)} />
                {(specificPriceRules?.arrSorted && specificPriceRules?.arrSorted?.length > 1 && allRowData?.myType === 'Sell') ? <CustomSelectWithSearchBar className='max-w-200 w-full mx-auto modal_complement_specific_price_input' label={t('priceZoneGroup', 'Zona de precio')} items={specificPriceRules?.dictionary || {}} value={specificPriceRule} onChange={(value) => mySpecificPriceRuleOnChange(value)} /> : <div className='max-w-200 w-full mx-auto modal_complement_specific_price_input'> </div>}
                <SearchBarProduct placeholder={t('addOtherProduct', 'Añadir otro producto')} changeSelectedItems={onAddAddonProduct} name='ProductId' id={''} url='/api/app/product/getAllProducts' where={{}} className=''/>
            </div>
            {renderThis}
        </ModalCreateEditExtraBig>
    )
}

interface ModalComplementProductOrderLogicProps {
    ProductId: number
    allRowData: RowData
    myOrderId?: number
}

const useModalComplementProductOrderLogic = ({ ProductId, allRowData, myOrderId }: ModalComplementProductOrderLogicProps) => {
    const [data, setData] = useState<DictEditProductAddonOrder | null>(null)
    const [searchValue, setSearchValue] = useState<string>('')
    const [specificPriceRule, setSpecificPriceRule] = useState<number>(allRowData?.completeData?.header?.SpecificPriceRuleIds?.[0])
    const { id } = useParams()
    const { t } = useTwinTranslation()
    const categoryBase = t('base', 'Base')
    const manualCategory = t('productsAddedManually', 'Productos añadidos manualmente')
    const categoriesDict = useMemo(() => getCategoriesDictionary() || {}, [])

    const onAddAddonProduct: OnChangeSearchBarAddProduct = useCallback((product) => {
        setData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            if (!copy.productAddons[0]){
                copy.productAddons[0] = { elements: [], name: manualCategory }
            }
            const myManualProductsIndex = copy.productAddons[0].elements.length+1
            copy.productAddons[0].elements.push({ ...product, ProductId, order: myManualProductsIndex, quantity: 1, myNaturalRowIndex: myManualProductsIndex - 1, ProductAddonId: product.id, fullName: product.reference + ' ' + product.name })
            return copy
        })
    }, [setData, manualCategory, ProductId])

    const mySpecificPriceRuleOnChange = useCallback((value: number) => {
        setSpecificPriceRule(value)
        setData(null)
    }, [setData, setSpecificPriceRule])

    const getData = useCallback(async () => {
        if (ProductId) {
            const ParentParentId = allRowData['Product.Product.id']
            let result = await twinFetchPostJSON(allRowData?.['url' + allRowData?.myType], {
                where: {
                    ProductId,
                    'SpecificPriceRuleId': specificPriceRule
                },
                order: [['order', 'asc']]
            })
            let suscribedLines: TwinDictionary = {}
            let parentLines: TwinDictionary = {}
            let suscribedVariables: TwinDictionary = {}
            let alreadyProcessed = new Set()
            const currentDictionary = allRowData?.completeData ? dictionaryComplexFromJsonArr(allRowData.completeData.tableData, 'Product.id', (param, allRowData) => param + '_' + (allRowData.OrderProductId), true, undefined, (item: any) => item.id !== allRowData.id && item.OrderProductId !== allRowData.id ) : {}
            const myVariables = dictionaryComplexFromJsonArr(allRowData.completeData?.header?.OrderVariables || [], 'OrderVariableId')
            if (result) {
                allRowData['_oldQuantity'] = allRowData?.quantity
                allRowData['_oldCalculatedQuantity'] = allRowData?.calculatedQuantity
                const addonsByCategory: TwinDictionary = {
                    'ParentProduct': {
                        elements: [
                            allRowData
                        ]
                    }
                }
                for (const element of result) {
                    let updateQuantity = element.ScaleByProductId ? true : false
                    const key = element.ProductAddonId + '_' + allRowData.id
                    const myElement = currentDictionary[key]
                    alreadyProcessed.add(key)
                    element.fullName = element.reference + ' ' + element.name
                    if (myElement) {
                        element.quantity = myElement.quantity
                        element.calculatedQuantity = myElement.calculatedQuantity
                        element.LineId = myElement.id
                        element.OrderId = myElement.OrderId
                        element.nLine = myElement?.nLine
                        element.manualPrice = myElement?.manualPrice
                        element.wasteWeight = myElement?.wasteWeight || 0
                        element['_oldQuantity'] = myElement?.quantity
                        element['_oldCalculatedQuantity'] = myElement?.calculatedQuantity
                        element['_alreadyHaveScale'] = myElement?.scaleQuantity ? true : false
                        element['ScaleOrderProductId'] = myElement?.ScaleOrderProductId
                        element.initialUnitPriceWithoutTax = myElement?.initialUnitPriceWithoutTax
                        updateQuantity = element['_alreadyHaveScale']
                    } else {
                        element.quantity = null
                        element.calculatedQuantity = null
                    }
                    if (element.ScaleByVariableId && !myVariables?.[element.ScaleByVariableId]) {
                        element.ScaleByVariableId = null
                        element.scaleQuantity = null
                    }
                    const CategoryId = element['ProductAddon.Categories.CategoryProduct.CategoryId'] || element['Category.CategoryLangs.CategoryId'] || element['ProductAddonBuy.Categories.CategoryProduct.CategoryId']
                    if (CategoryId) {
                        const orderCategory = categoriesDict[CategoryId]?.absoluteOrder
                        if (!addonsByCategory[orderCategory]) {
                            addonsByCategory[orderCategory] = { elements: [], name: element['ProductAddon.Categories.CategoryLangs.name'] || element['ProductAddonBuy.Categories.CategoryLangs.name'] || categoryBase }
                        }
                        const myScaleProductId = element.ScaleByProductId ? parseInt(element.ScaleByProductId) === parseInt(ParentParentId || '0') ? ProductId : element.ScaleByProductId : element.ScaleByProductId
                        element.ScaleByProductId = myScaleProductId
                        addonsByCategory[orderCategory].elements.push({ ...element, updateQuantity, myNaturalRowIndex: addonsByCategory[orderCategory].elements.length})
                        const myLength = String(addonsByCategory[orderCategory].elements.length - 1)
                        if (myScaleProductId){
                            if (!suscribedLines[myScaleProductId]){
                                suscribedLines[myScaleProductId] = []
                            }
                            suscribedLines[myScaleProductId].push(String(orderCategory) + '-' + myLength)
                        } else if (element.ScaleByVariableId && myVariables?.[element.ScaleByVariableId]) {
                            suscribedVariables[element.ProductAddonId] = parseFloat(myVariables?.[element.ScaleByVariableId].value) * parseFloat(element.scaleQuantity)
                        }
                        parentLines[element.ProductAddonId] = (String(orderCategory) + '-' + myLength)
                    }
                }
                parentLines[ProductId] = ('ParentProduct-0')
                for (const key in currentDictionary) {
                    if (key !== ProductId +'_null' && !alreadyProcessed.has(key)){
                        const myElement = currentDictionary[key]
                        if (!addonsByCategory[0]) {
                            addonsByCategory[0] = { elements: [], name: manualCategory }
                        }
                        const myManualProductsIndex = addonsByCategory[0].elements.length + 1
                        addonsByCategory[0].elements.push({
                            ...myElement,
                            quantity:  myElement.quantity,
                            calculatedQuantity:  myElement.calculatedQuantity,
                            LineId: myElement.id,
                            OrderId: myElement.OrderId,
                            nLine: myElement?.nLine,
                            order: myManualProductsIndex,
                            myNaturalRowIndex: myManualProductsIndex - 1,
                            ProductAddonId: myElement.ProductId,
                            fullName: myElement.reference + ' ' + myElement.name,
                            wasteWeight: myElement.wasteWeight || 0,
                            initialUnitPriceWithoutTax: myElement?.initialUnitPriceWithoutTax
                        })
                    }
                }
                setData({ productAddons: addonsByCategory, suscribedLines, parentLines, suscribedVariables })
            }
        }
    }, [ProductId, allRowData, categoryBase, categoriesDict, manualCategory, specificPriceRule])

    const beforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        const result: TwinDictionary[] = []
        if (data?.productAddons) {
            for (const key in data?.productAddons) {
                if (data?.productAddons?.[key]?.elements) {
                    if (key === 'ParentProduct') {
                        const element = data?.productAddons?.[key]?.elements?.[0]
                        const myQuantity = parseFloat(String(element.quantity || '0'))
                        const myCalculatedQuantity = parseFloat(String(element.calculatedQuantity || '0'))
                        const myQuantity2 = parseFloat(String(element.quantity2 || '0'))
                        const isChanging = parseFloat(String(element['_oldQuantity'] || '0')) !== myQuantity
                        let myItem: TwinDictionary = {
                            initialUnitPriceWithoutTax: element.initialUnitPriceWithoutTax || 0,
                            quantity: myQuantity,
                            calculatedQuantity: myCalculatedQuantity,
                            quantity2: myQuantity2,
                            OrderId: myOrderId || id,
                            ProductId: element.ProductId,
                            OrderProductId: allRowData.id,
                            nLine: element?.nLine,
                            id: element?.id,
                            isParent: true,
                            isChanging,
                            _oldQuantity: element._oldQuantity,
                            manualPrice: element.manualPrice,
                        }
                        result.push(myItem)
                    } else {
                        for (const element of data?.productAddons?.[key]?.elements) {
                            const myQuantity = parseFloat(String(element.quantity || '0'))
                            const myCalculatedQuantity = parseFloat(String(element.calculatedQuantity || '0'))
                            const isChanging = parseFloat(String(element["_oldQuantity"] || '0')) !== myQuantity || element['_alreadyHaveScale'] !== element.updateQuantity
                            const myQuantity2 = parseFloat(String(element.quantity2 || '0'))
                            if ((element.quantity && parseFloat(element.quantity) !== 0) || element.LineId) {
                                let myItem: TwinDictionary = {
                                    initialUnitPriceWithoutTax: element.initialUnitPriceWithoutTax || 0,
                                    quantity: myQuantity,
                                    calculatedQuantity: myCalculatedQuantity,
                                    quantity2: myQuantity2,
                                    OrderId: myOrderId || id,
                                    ProductId: element.ProductAddonId,
                                    OrderProductId: allRowData.id,
                                    nLine: element?.nLine,
                                    ScaleOrderProductId: element.updateQuantity ? element.ScaleOrderProductId : undefined,
                                    ScaleRelationProductId: element.updateQuantity ? element.ScaleByProductId : undefined,
                                    isChanging,
                                    _oldQuantity: element._oldQuantity,
                                    ScaleVariableId: element.updateQuantity ? element.ScaleByVariableId : undefined,
                                    scaleQuantity: element.updateQuantity ? element.scaleQuantity : undefined,
                                    updateQuantity: element.updateQuantity,
                                    manualPrice: element.manualPrice
                                }
                                if (element.LineId) {
                                    myItem.id = element.LineId
                                } else if (allRowData?.OrderAccountId) {
                                    myItem.OrderAccountId = allRowData?.OrderAccountId
                                }
                                if (allRowData.OrderVariantGroupId) {
                                    myItem.OrderVariantGroupId = allRowData.OrderVariantGroupId
                                }
                                if (allRowData.OrderAddonGroupId) {
                                    myItem.OrderAddonGroupId = allRowData.OrderAddonGroupId
                                }
                                result.push(myItem)
                            }
                        }
                    }
                }
            }
        }
        const sortedByNLines = sortTableDataByNLines(result)
        vals.createProductBulk = sortedByNLines
        return true
    }, [id, data, allRowData, myOrderId])

    useEffect(() => {
        if (data === null) {
            getData()
        }
    }, [getData, data])

    return { data, setData, beforeSubmit, searchValue, setSearchValue, onAddAddonProduct, specificPriceRule, mySpecificPriceRuleOnChange }
}

interface ParentProductProps extends ParentProductLogicProps {
    data: DictEditProductAddonOrder
}

const ParentProduct: React.FC<ParentProductProps> = ({ data, ...logic }) => {
    const { onChangeQuantity } = useParentProductLogic({ ...logic })
    const myParentProduct = data?.productAddons?.ParentProduct?.elements?.[0]
    return (
        <div className='flex items-center parent_product_addon_container'>
            <span className='parent_product_addon_name regular16'>{myParentProduct?.name}</span>
            <InputQuantityButtons value={myParentProduct?.quantity || 0} onChange={onChangeQuantity} minQuantity={myParentProduct?.minQuantity} />
        </div>
    )
}

interface ParentProductLogicProps {
    setData: React.Dispatch<React.SetStateAction<DictEditProductAddonOrder | null>>
}

const useParentProductLogic = ({ setData }: ParentProductLogicProps) => {
    const onChangeQuantity = useCallback((val: string) => {
        setData((old)=>{
            if(!old){
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            let myQuantity: any = val
            let myCalculatedQuantity: any = val
            const parentProduct = copyOld.productAddons.ParentProduct.elements[0]
            if (parentProduct) {
                const minQuantity = parseFloat(parentProduct.minQuantity || '0')
                let parsedVal = parseFloat(String(val || '0'))
                if (parsedVal < minQuantity) {
                    myQuantity = String(minQuantity)
                    parsedVal = minQuantity
                }
                myCalculatedQuantity = roundNumberAndDecimals(parsedVal + ((parentProduct.wasteWeight || 0) * parsedVal / 100), totalCalculateParams)
            }
            if(copyOld.productAddons.ParentProduct.elements[0]){
                copyOld.productAddons.ParentProduct.elements[0].quantity = String(myQuantity)
                copyOld.productAddons.ParentProduct.elements[0].quantity2 = roundNumberAndDecimals(myQuantity * parseFloat(copyOld.productAddons.ParentProduct.elements[0].quantity2base || '0'), baseCalculateParams)
                copyOld.productAddons.ParentProduct.elements[0].calculatedQuantity = String(myCalculatedQuantity)
            }
            const parentProductId = copyOld.productAddons.ParentProduct.elements[0].ProductId
            checkIfScale(copyOld, parentProductId, copyOld.productAddons.ParentProduct.elements[0])
            const haveProductParentId = copyOld.productAddons.ParentProduct.elements[0]?.["Product.ProductId"]
            if (haveProductParentId) {
                checkIfScale(copyOld, haveProductParentId, copyOld.productAddons.ParentProduct.elements[0])
            }
            return copyOld
        })
    }, [setData])
    return { onChangeQuantity }
}

interface SingleTableAddonProps extends SingleTableAddonLogicProps {
    searchValue: string
    data: DictEditProductAddonOrder
    elements: TwinDictionary[]
    categoryName: string
    type: 'Buy' | 'Sell'
    specificPriceRules: SpecificPriceRulesPayload
}

const SingleTableAddon: React.FC<SingleTableAddonProps> = ({ type, data, elements, categoryName, searchValue, specificPriceRules, ...logic }) => {
    const { t } = useTwinTranslation()
    const defaultSpecificPriceRuleId = useMemo(() => {
        const specificPriceRulesSorted = specificPriceRules?.arrSorted || []
        return specificPriceRulesSorted?.[0]?.id
    }, [specificPriceRules?.arrSorted])
    const { onChangeQuantity, onChangeUpdateQuantity, onChangeActiveProductAddon } = useSingleTableAddonLogic({ ...logic })
    let displayColumnUpdate = data.suscribedLines && Object.keys(data.suscribedLines).length > 0
    const priceColumns: ColumnConfiguration<AddonProductOrderTy>[] = []
    const isSell = type === 'Sell'
    if (isSell) {
        priceColumns.push(
            {
                id: 'price',
                dataKey: 'SpecificPriceRuleId',
                label: (props) => {
                    const myValue = parseInt(props.selectedFilterValue || props.value || defaultSpecificPriceRuleId)
                    return <span>{t('price', 'Precio') + ' (' + (specificPriceRules?.dictionary?.[myValue]?.name || t('notSelectedPriceRule', 'Sin tarifa selec.')) + ')'} </span>
                },
                simpleLabel: t('price', 'Precio'),
                searchableComponent: {
                    component: 'SearchableComponentsCustomSelectSingle',
                    extraComponentData: { items: specificPriceRules?.dictionary || {}, label: t('specificPriceRule', 'Tarifa') }
                },
                minWidth: 100,
                generalSearchable: false,
                customRender: (_parameterValue, allRowData) => {
                    return <DisplayPriceGettingSpecificPriceParams value={allRowData?.calculated_price} CurrencyId={specificPriceRules?.dictionary[allRowData.SpecificPriceRuleId]?.CurrencyId || ''} with_vat={false} specificPriceRules={specificPriceRules} SpecificPriceRuleId={defaultSpecificPriceRuleId} />
                }
            }
        )
    }

    const columns: ColumnTableSchema<AddonProductOrderTy> = [
        {
            id: 'createDefault',
            dataKey: 'createDefault',
            label: '',
            customRender: (parameterValue, allRowData) => (
                <CheckboxMargin checked={allRowData.quantity !== null} onChange={(value) => onChangeActiveProductAddon(value, allRowData.myNaturalRowIndex)} label={''} />
            ),
            width: 45
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            customRender: (parameterValue, allRowData) => (
                <div className='cursor-pointer flex-auto h-full items-center flex text-green-00 max-w-full' title={parameterValue} onClick={() => onChangeActiveProductAddon(allRowData.quantity === null, allRowData.myNaturalRowIndex)}><span className='twin_elipsis'>{parameterValue}</span></div>
            ),
            width: 175
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            customRender: (parameterValue, allRowData) => (
                <div className='cursor-pointer flex-auto h-full items-center flex' onClick={() => onChangeActiveProductAddon(allRowData.quantity === null, allRowData.myNaturalRowIndex)}>{parameterValue}</div>
            ),
            width: isSell ? 400 : 534
        },
        ...priceColumns,
        {
            id: 'updateQuantity',
            dataKey: 'updateQuantity',
            label: displayColumnUpdate ? t('sync', 'Sincronizar') : '',
            customRender: (parameterValue, allRowData) => (
                showCheckboxUpdateQuantity(allRowData, data) ? <CheckboxMargin className='mx-auto' checked={String(parameterValue) === 'true'} onChange={(value) => onChangeUpdateQuantity(value, allRowData.myNaturalRowIndex)} label={''} /> : <div></div>
            ),
            width: 100
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: () => <span className='text-center' style={{width: 130}}>{t('quantity', 'Cantidad')}</span>,
            customRender: (parameterValue, allRowData) => (
                allRowData.quantity !== null ?
                <div>
                    <InputQuantityButtons value={parameterValue} onChange={(value) => onChangeQuantity(value, allRowData.myNaturalRowIndex)} />
                </div> : <div></div>
            ),
            width: 175
        },
    ]
    const myElements = filterElementsBySearchEditAddonProducts(elements, searchValue)
    if(!myElements?.length){
        return null
    }
    return (
        <div className='single_table_addon'>
            <span className='regular16 mb-10 block'>{categoryName}</span>
            <TableWithoutVirtualListing data={myElements} columns={columns} disableScroll={true} className='table_small_padding' />
        </div>
    )
}

interface SingleTableAddonLogicProps {
    setData: React.Dispatch<React.SetStateAction<DictEditProductAddonOrder | null>>
    categoryOrder: string
}

const useSingleTableAddonLogic = ({ setData, categoryOrder }: SingleTableAddonLogicProps) => {

    const onChangeUpdateQuantity = useCallback((value: boolean, rowIndex: number) => {
        setData((oldProductData) => {
            if (!oldProductData) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(oldProductData))
            const productAddon = copy?.productAddons?.[categoryOrder]?.elements?.[rowIndex]
            if (!productAddon?.['updateQuantity']) {
                productAddon['updateQuantity'] = true
            }

            if (productAddon?.['updateQuantity']) {
                productAddon['updateQuantity'] = value
            }
            if(value === true){
                if (productAddon.ScaleByProductId) {
                    updateQuantityScaled(copy, productAddon)
                } else if (productAddon.ScaleByVariableId) {
                    updateQuantityScaledVariables(copy, productAddon)
                }
                checkIfScale(copy, productAddon?.ProductAddonId, productAddon, true)
            }
            return copy
        })
    }, [setData, categoryOrder])

    const onChangeActiveProductAddon = useCallback((value: boolean, rowIndex: number) => {
        setData((oldProductData) => {
            if (!oldProductData) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(oldProductData))
            const productAddon = copy?.productAddons[categoryOrder]?.elements?.[rowIndex]
            if (value) {
                productAddon.calculatedQuantity = 1
                productAddon.quantity = 1
                productAddon.updateQuantity = true
                if(productAddon.ScaleByProductId){
                    updateQuantityScaled(copy, productAddon)
                } else if (productAddon.ScaleByVariableId) {
                    updateQuantityScaledVariables(copy, productAddon)
                }
                checkIfScale(copy, productAddon?.ProductAddonId, productAddon, true)
            }else {
                productAddon.quantity = null
                productAddon.calculatedQuantity = null
                checkIfScale(copy, productAddon?.ProductAddonId, productAddon, true)
            }
            return copy
        })
    }, [setData, categoryOrder])

    const onChangeQuantity = useCallback((value: string, rowIndex: number) => {
        setData((oldProductData) => {
            if (!oldProductData) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(oldProductData))
            const productAddon = copy?.productAddons[categoryOrder]?.elements?.[rowIndex]
            if (!productAddon?.quantity) {
                productAddon.quantity = 1
                productAddon.calculatedQuantity = 1
            }

            if (productAddon?.quantity) {
                let myCalculatedQuantity: any = value
                let myQuantity: any = value
                const minQuantity = parseFloat(productAddon.minQuantity || '0')
                let parsedVal = parseFloat(String(value || '0'))
                if (parsedVal < minQuantity) {
                    myQuantity = String(minQuantity)
                    parsedVal = minQuantity
                }
                myCalculatedQuantity = roundNumberAndDecimals(parsedVal + ((productAddon.wasteWeight || 0) * parsedVal / 100), totalCalculateParams)
                productAddon.quantity = myQuantity
                productAddon.calculatedQuantity = myCalculatedQuantity
                productAddon.quantity2 = roundNumberAndDecimals(myQuantity * parseFloat(productAddon.quantity2base || '0'), baseCalculateParams)
                checkIfScale(copy, productAddon?.ProductAddonId, productAddon)
            }
            productAddon.updateQuantity = false
            return copy
        })
    }, [setData, categoryOrder])

    return { onChangeQuantity, onChangeUpdateQuantity, onChangeActiveProductAddon }
}

const modalComplementProductOrderDispatch = {
    setCategories: (payload: CategoriesPayload) => ({ type: 'CHANGE_CATEGORIES', payload }),
    setSpecificPriceRules: (payload: SpecificPriceRulesPayload) => ({ type: 'CHANGE_SPECIFICPRICERULES', payload }),
}

export type ReduxOrderSellLayout = ConnectedProps<typeof modalComplementProductOrderConnect>
const mapModalComplementProductOrderConnector = (state: AllReduxPayloads) => ({ categories: state.categories, specificPriceRules: state.specificPriceRules })
const modalComplementProductOrderConnect = connect(mapModalComplementProductOrderConnector, modalComplementProductOrderDispatch)
const modalComplementProductOrderConnectLoading = withLoading(ModalComplementProductOrder, [{ fetchUrl: '/api/app/product/category/getAllCategories', propName: 'categories', setFunctionName: 'setCategories' }, { fetchUrl: '/api/app/product/specificPrice/getAllSpecificPriceRules', propName: 'specificPriceRules', setFunctionName: 'setSpecificPriceRules' }])
const ModalComplementProductOrderConnect = modalComplementProductOrderConnect(modalComplementProductOrderConnectLoading)


export default ModalComplementProductOrderConnect